import { useEffect, useRef, useState } from 'react';

import { RichViewer } from './RichEditor';

// 통합 탭 «PDF 저장» 전용 인쇄 보기(#3130 1번) — 옛 사이트 출력본(1026.pdf) 모양.
// 🔴 화면에 늘 그려 두지 않는다 — UnifiedTab 이 «PDF 저장»을 누를 때만 document.body 포털로 마운트하고
//    print() 뒤 바로 언마운트한다(늘 그리면 r6 시험의 텍스트·대체글 조회가 중복되고 이미지 요청이 두 배가 된다).
// 🔴 포털이 돌려주는 최상위 DOM 은 [data-exec-print-root] div 하나다(<style> 은 그 안) — 감싸개를 두면 감싸개째 숨는다.

export const PRINT_READY_TIMEOUT_MS = 30000;
const PRINT_READY_POLL_MS = 50;
const MAX_KPIS = 100;
const MAX_KPI_TEXT = 255;
const ACCENT = '#2d5bff';
const MUTED = '#8a93a6';
const THEMES = [
  { number: 1, title: 't1_title', items: [['t1i1_label', 't1i1'], ['t1i2_label', 't1i2']] },
  { number: 2, title: 't2_title', items: [['t2i1_label', 't2i1'], ['t2i2_label', 't2i2']] },
  { number: 3, title: 't3_title', items: [['t3i1_label', 't3i1'], ['t3i2_label', 't3i2']] },
];

// 🔴 absolute·visibility 방식은 쓰지 않는다(조상에 position·transform 이 생기면 조용히 1쪽만 찍힌다).
// 🔴 부서 블록 전체에 break-inside: avoid 를 걸지 않는다(옛 CSS 는 그래서 1쪽이 머리만 남고 비었다) — 둘째 부서부터 새 쪽.
const PRINT_CSS = `
@page { size: A4 landscape; margin: 12mm; }
@media screen { [data-exec-print-root] { display: none; } }
@media print {
  body > *:not([data-exec-print-root]) { display: none !important; }
  [data-exec-print-root] { display: block !important; position: static; }
  [data-exec-print-root] .exec-print-dept + .exec-print-dept { break-before: page; }
  [data-exec-print-root] .exec-print-dept-head { break-after: avoid; }
  [data-exec-print-root] img, [data-exec-print-root] table, [data-exec-print-root] .exec-print-item-title { break-inside: avoid; }
  [data-exec-print-root] img { max-width: 100%; max-height: 400px; object-fit: contain; }
}
[data-exec-print-root] .exec-print-dept-head { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const sectionTitleStyle = { marginTop: '18px', marginBottom: '8px', color: ACCENT, fontSize: '14px', fontWeight: 700 };
const itemTitleStyle = { marginTop: '10px', marginBottom: '4px', color: '#1f2937', fontSize: '13px', fontWeight: 700 };
const emptyStyle = { color: MUTED, fontSize: '13px', fontStyle: 'italic' };
const cellStyle = { padding: '6px 10px', border: '1px solid #d5dae5', textAlign: 'left' };

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 그림 하나가 끝났나(받음·실패 모두 끝) — decode 는 있을 때만(jsdom 엔 없다).
function imageSettled(img) {
  if (typeof img.decode === 'function') return img.decode().catch(() => {});
  if (img.complete) return Promise.resolve();
  return new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
  });
}

/**
 * 인쇄 루트가 찍을 준비가 됐나 기다린다 — ① 루트 안 상태 표시([role="status"] · RichViewer «본문을 불러오는 중입니다.»)가
 * 0개가 될 때까지 ② 그 뒤 모든 그림이 끝날 때까지. 전체 상한 timeoutMs.
 * 결과: {ready: true} · {cancelled: true} · {ready: false, pending: N}(상한 초과 — 못 받은 그림 수 · 아직 준비 중인 본문 칸도 1개로 센다).
 */
export async function waitForPrintReady(root, { timeoutMs = PRINT_READY_TIMEOUT_MS, isCancelled = () => false } = {}) {
  const startedAt = Date.now();
  const timedOut = () => Date.now() - startedAt >= timeoutMs;

  while (root.querySelector('[role="status"]')) {
    if (isCancelled()) return { cancelled: true };
    if (timedOut()) {
      const loadingBodies = root.querySelectorAll('[role="status"]').length;
      const loadingImages = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete).length;
      return { ready: false, pending: loadingBodies + loadingImages };
    }
    await sleep(PRINT_READY_POLL_MS);
  }

  const images = Array.from(root.querySelectorAll('img'));
  const settled = new Set();
  images.forEach((img) => {
    imageSettled(img).then(() => settled.add(img));
  });
  while (settled.size < images.length) {
    if (isCancelled()) return { cancelled: true };
    if (timedOut()) return { ready: false, pending: images.length - settled.size };
    await sleep(PRINT_READY_POLL_MS);
  }
  if (isCancelled()) return { cancelled: true };
  return { ready: true };
}

function boundedText(value) {
  try {
    return String(value ?? '').slice(0, MAX_KPI_TEXT);
  } catch {
    return '';
  }
}

function kpiRows(value) {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_KPIS).map((item) => ({
    label: boundedText(item?.label),
    value: boundedText(item?.value),
    unit: boundedText(item?.unit),
  }));
}

// SI 단위(1 kB = 1000 B).
function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 ** 2) return `${(bytes / 1000).toFixed(1)} kB`;
  if (bytes < 1000 ** 3) return `${(bytes / 1000 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1000 ** 3).toFixed(1)} GB`;
}

// 리치 칸 — 비면 RichViewer 를 부르지 않고 «(내용 없음)»(RichViewer 의 «등록된 내용이 없습니다.»와 다르다).
function RichBlock({ html, ariaLabel }) {
  if (!html) return <div style={emptyStyle}>(내용 없음)</div>;
  return <RichViewer initialHTML={html} ariaLabel={ariaLabel} />;
}

function PrintDept({ report }) {
  const dept = report.dept || '부서 미지정';
  const kpis = kpiRows(report.kpis);
  const files = Array.isArray(report.files) ? report.files : [];
  const meta = `작성자 ${report.author || '미입력'}${report.report_date ? ` · ${report.report_date}` : ''}`;

  return (
    <section className="exec-print-dept" style={{ marginTop: '18px', border: '1px solid #d5dae5', borderRadius: '10px' }}>
      <div className="exec-print-dept-head" style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', padding: '14px 20px', background: ACCENT, color: '#fff', borderRadius: '9px 9px 0 0' }}>
        <strong style={{ fontSize: '16px' }}>{dept}</strong>
        <span style={{ fontSize: '12.5px', opacity: 0.9 }}>{meta}</span>
      </div>
      <div style={{ padding: '20px' }}>
        {THEMES.map((theme) => (
          <div key={theme.number}>
            <div style={theme.number === 1 ? { ...sectionTitleStyle, marginTop: 0 } : sectionTitleStyle}>
              관리 테마 {theme.number}{report[theme.title] ? ` · ${report[theme.title]}` : ''}
            </div>
            {theme.items.map(([labelField, richField], index) => (
              <div key={richField}>
                <div className="exec-print-item-title" style={itemTitleStyle}>
                  항목 {index + 1}{report[labelField] ? ` · ${report[labelField]}` : ''}
                </div>
                <RichBlock html={report[richField] || ''} ariaLabel={`${dept} 인쇄 테마 ${theme.number} 항목 ${index + 1} 본문`} />
              </div>
            ))}
          </div>
        ))}

        <div style={sectionTitleStyle}>AI 활용 사례</div>
        <RichBlock html={report.ai_usage || ''} ariaLabel={`${dept} 인쇄 AI 활용 사례 본문`} />

        <div style={sectionTitleStyle}>KPI / 핵심 지표</div>
        {!kpis.length ? (
          <div style={emptyStyle}>(KPI 없음)</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['지표', '값', '단위'].map((value) => (
                  <th key={value} scope="col" style={{ ...cellStyle, background: '#f3f5fa', fontWeight: 700 }}>{value}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {kpis.map((kpi, index) => (
                <tr key={index}>
                  <td style={cellStyle}>{kpi.label}</td>
                  <td style={cellStyle}>{kpi.value}</td>
                  <td style={cellStyle}>{kpi.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={sectionTitleStyle}>기타 / 특이사항</div>
        <RichBlock html={report.etc || ''} ariaLabel={`${dept} 인쇄 기타 본문`} />

        {files.length > 0 && (
          <>
            <div style={sectionTitleStyle}>첨부 파일</div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
              {files.map((file, index) => {
                const size = formatBytes(file?.bytes);
                return (
                  <li key={file?.id || `${file?.name}-${index}`} style={{ marginTop: '3px' }}>
                    {file?.name || 'download'}{size ? ` (${size})` : ''}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * 선택 주차 스냅숏을 옛 출력본 모양으로 그린다. 마운트 뒤(DOM 이 생긴 뒤) onRootReady(root) 를 한 번 부른다 —
 * 클릭 처리기 안에서 루트를 찾으면 React 18 포털은 같은 틱에 DOM 이 없어 null 이다.
 */
export default function UnifiedPrintView({ weekLabel, reports, onRootReady }) {
  const rootRef = useRef(null);
  const onRootReadyRef = useRef(onRootReady);
  onRootReadyRef.current = onRootReady;
  const [issuedAt] = useState(() => new Date().toLocaleDateString('ko-KR'));

  useEffect(() => {
    if (rootRef.current) onRootReadyRef.current?.(rootRef.current);
  }, []);

  return (
    <div data-exec-print-root="" ref={rootRef} style={{ color: '#1f2937', background: '#fff', lineHeight: 1.6 }}>
      <style>{PRINT_CSS}</style>
      <div style={{ textAlign: 'center', marginBottom: '6px' }}>
        <div style={{ fontSize: '22px', fontWeight: 800 }}>통합 주간 보고서 · {weekLabel}</div>
        <div style={{ marginTop: '4px', fontSize: '13px' }}>발행일: {issuedAt}</div>
      </div>
      {(Array.isArray(reports) ? reports : []).map((report) => <PrintDept key={report.id} report={report} />)}
    </div>
  );
}
